import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

// All real Overlander Mobility photos uploaded for this build.
const PHOTOS = [
  {
    src: "https://customer-assets-4nw71qhi.emergentagent.net/job_aussie-4wd-hub/artifacts/5j9i1gcn_Joshua%20Cady%202.png",
    title: "Off the track, into the scree",
    location: "Trackside descent · WA",
    credit: "Joshua Cady",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_aussie-4wd-hub/artifacts/bpk3qwsq_Overlander-4zs-Mt-Kosciuszko-20190430-2-650.jpg",
    title: "Mt Kosciuszko summit",
    location: "Kosciuszko NP, NSW · 2,228 m",
    credit: "Overlander Mobility",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_aussie-4wd-hub/artifacts/m8pd42s9_Flatlander%20Full%20frame.png",
    title: "Off-road tyres. On-road manners.",
    location: "Class 3 · Village high street",
    credit: "TerrainHopper UK",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_aussie-4wd-hub/artifacts/t9lx6a9j_OM-demonstrator-kosciuszko-2019-3.jpg",
    title: "Main Range alpine track",
    location: "Mt Kosciuszko, NSW · 2019",
    credit: "Overlander Mobility",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_aussie-4wd-hub/artifacts/ne421xeh_TERRAINHOPPER-monster-wheels-background.webp",
    title: "Your seat is waiting.",
    location: "The Overlander · Factory-fresh",
    credit: "TerrainHopper UK",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_aussie-4wd-hub/artifacts/pwsof0a4_parker-river-cape-otway.jpg",
    title: "Parker River estuary",
    location: "Cape Otway, VIC",
    credit: "Overlander Mobility",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_aussie-4wd-hub/artifacts/n9tyxwvj_parker-river-cape-otway-down-steps.jpg",
    title: "Parker River bush steps",
    location: "Cape Otway, VIC",
    credit: "Overlander Mobility",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_aussie-4wd-hub/artifacts/z79u56fe_parker-river-cape-otway-beach.jpg",
    title: "Parker River beach",
    location: "Cape Otway, VIC",
    credit: "Overlander Mobility",
  },
];

export default function Gallery() {
  const trackRef = useRef(null);

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.85;
    el.scrollBy({ left: dir === "next" ? amount : -amount, behavior: "smooth" });
  };

  return (
    <section id="gallery" data-testid="gallery" className="relative py-24 md:py-32 bg-muted">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
            Field Gallery
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight font-bold leading-[1.05]">
            The Overlander, <br /><span className="text-primary">in the wild.</span>
          </h2>
          <p className="mt-4 text-sm md:text-base text-muted-foreground max-w-md leading-relaxed">
            Real photos from Overlander Mobility expeditions and trials across Australia — from
            Cape Otway rainforest to the Bunda Cliffs and the Kosciuszko summit.
          </p>
        </div>

        <figure
          data-testid="gallery-hero"
          className="mt-12 lg:mt-14 relative overflow-hidden rounded-sm border border-border group"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_aussie-4wd-hub/artifacts/myob4nxb_overlander-bunda-cliffs.jpg"
            alt="Overlander 4 parked on the edge of the Bunda Cliffs, Great Australian Bight"
            className="w-full h-[420px] md:h-[560px] lg:h-[640px] object-cover transition-transform duration-700 group-hover:scale-[1.02]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
          <div className="absolute top-4 right-4">
            <span
              data-testid="gallery-hero-credit"
              className="inline-block text-[9px] uppercase tracking-[0.2em] font-bold px-2 py-1 rounded-sm bg-black/50 text-white backdrop-blur-sm"
            >
              Photo · Overlander Mobility
            </span>
          </div>
          <figcaption className="absolute inset-x-0 bottom-0 p-6 md:p-10 lg:p-14">
            <div className="max-w-3xl">
              <span className="inline-block text-[11px] uppercase tracking-[0.25em] font-bold text-primary-foreground bg-primary px-2.5 py-1 rounded-sm">
                Bunda Cliffs · Great Australian Bight
              </span>
              <p className="mt-4 font-display text-2xl md:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-white leading-[1.05]">
                One machine. All edges of the continent.
              </p>
              <p className="mt-4 text-sm md:text-base text-white/80 max-w-xl leading-relaxed">
                The Overlander 4 at the top of the Bunda Cliffs on the Nullarbor — 90 metres of
                sheer limestone above the Southern Ocean. You won&apos;t find many wheelchairs
                this far from the bitumen.
              </p>
            </div>
          </figcaption>
        </figure>
      </div>

      <div className="mt-12 lg:mt-14">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 mb-5 hidden md:flex items-center justify-between gap-4">
          <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-muted-foreground">
            Scroll or use the arrows
          </p>
          <div className="flex items-center gap-2">
            <Button
              data-testid="gallery-prev-btn"
              size="icon"
              variant="outline"
              onClick={() => scroll("prev")}
              className="rounded-sm h-11 w-11"
              aria-label="Previous photo"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button
              data-testid="gallery-next-btn"
              size="icon"
              variant="outline"
              onClick={() => scroll("next")}
              className="rounded-sm h-11 w-11"
              aria-label="Next photo"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </div>
        <div
          ref={trackRef}
          data-testid="gallery-track"
          className="flex gap-5 overflow-x-auto pb-4 px-6 lg:px-10 snap-x snap-mandatory scroll-smooth [scrollbar-width:thin]"
        >
          {PHOTOS.map((p, i) => (
            <figure
              key={p.src}
              data-testid={`gallery-item-${i}`}
              className="snap-start shrink-0 w-[85%] sm:w-[70%] md:w-[58%] lg:w-[44%] xl:w-[38%] group"
            >
              <div className="relative aspect-[4/3] overflow-hidden rounded-sm border border-border bg-card">
                <img
                  src={p.src}
                  alt={`${p.title} — ${p.location}`}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-transparent" />
                {p.credit && (
                  <div className="absolute top-3 right-3">
                    <span
                      data-testid={`gallery-credit-${i}`}
                      className="inline-block text-[9px] uppercase tracking-[0.2em] font-bold px-2 py-1 rounded-sm bg-black/50 text-white backdrop-blur-sm"
                    >
                      Photo · {p.credit}
                    </span>
                  </div>
                )}
                <figcaption className="absolute inset-x-0 bottom-0 p-5 md:p-6">
                  <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-primary-foreground bg-primary inline-block px-2 py-0.5 rounded-sm">
                    {p.location}
                  </div>
                  <div className="mt-2 font-display text-lg md:text-xl text-white font-semibold uppercase tracking-tight">
                    {p.title}
                  </div>
                </figcaption>
              </div>
            </figure>
          ))}
          <div className="shrink-0 w-6" aria-hidden="true" />
        </div>
      </div>
    </section>
  );
}
